import logError from 'src/data/services/logError';
import React, { useState, useEffect } from 'react';
import { Text, StyleSheet, View, Platform, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import _ from 'lodash';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import Loader from 'src/presentation/components/atoms/Loader';
import ProgramItem from 'src/presentation/components/molecules/ProgramItem';
import TimetableBlocks from 'src/presentation/components/organisms/TimetableBlocks';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMyTimeTableStore } from 'src/domain/stores/useMyTimeTableStore';
import { useMyBarTimeTableStore } from 'src/domain/stores/useMyBarTimeTableStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList, TimeTablePageItem } from 'src/presentation/navigation/types/NavigationTypes';
import { navigatePage } from 'src/presentation/helpers/NavigationHelper';
import Fonts from 'src/core/utils/Fonts';
import Colors from 'src/core/utils/Colors';
import isFavoritesPage from 'src/core/utils/isFavoritesPage';
import type Constants from 'src/core/utils/Constants';
import { formatPageDate } from 'src/core/utils/formatTimetableTime';
import type { ApiPageProgramHolder, ApiPageProgramItem } from 'src/core/models/api/ApiPage';

function ProgramScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenProgram>>();
	const eventStore = useEventStore();
	const myTimeTableStore = useMyTimeTableStore();
	const myBarTimeTableStore = useMyBarTimeTableStore();
	const navigationStore = useNavigationStore();
	const analyticsStore = useAnalyticsStore();
	const { page, isLoading: isPageLoading } = usePageFromRoute(route?.params, { type: 'Program' });
	const title: string = page?.name ?? '';
	const pageID: number = page?.id ?? 0;

	// The event (incl. this program holder) is already loaded in the store when we arrive from
	// the program grid. Render straight from memory so we skip the blocking re-fetch: re-fetching
	// toggles the global loading flag, which also flickers the outgoing grid screen mid-transition.
	const programHolders = eventStore.activeEventView.filter((p): p is ApiPageProgramHolder => p.type === 'ProgramHolder');
	const programLoaded = programHolders.length > 0;
	// Derived, not a snapshot taken at mount: from a URL the pages arrive AFTER the first render,
	// and the branch that used to clear the flag is skipped once they are there -- which left the
	// screen spinning forever on a shared link.
	const [hasFetched, setHasFetched] = useState(programLoaded);
	const isLoading = !programLoaded && !hasFetched;
	const [refreshing, setRefreshing] = useState(false);
	// Mijn timetable is built entirely from the favorites, and the server is leading for those: rendering an
	// empty list first made the whole screen jump the moment the server answered. Which event that answer
	// belongs to, so switching events waits again instead of showing the previous one's empty state.
	const [favoritesSyncedFor, setFavoritesSyncedFor] = useState('');
	const footerHeight = useFooterHeight();
	// null until the participant picks a view themselves, so the holder stays leading until then.
	const [prefersBlocks, setPrefersBlocks] = useState<boolean | null>(null);

	// An event can carry several holders (say "Programma" next to "Blokkenschema"), so pick the one this day lives
	// under; the first one stays the fallback for Mijn timetable and the empty state.
	const programHolder = programHolders.find((h) => h.children.some((c) => c.id === pageID)) ?? programHolders[0];
	// Favorites are stored per event, so Mijn timetable walks the days of every holder.
	const programDays = programHolders.flatMap((h) => h.children);
	const programDay = programHolder?.children.find((p) => p.id === pageID);
	const blocks = programDay?.data.timetable ?? [];
	const blocksStart = programDay?.data.starttime ?? 0;
	const blocksEnd = programDay?.data.endtime ?? 0;
	const isMyTimetable = isFavoritesPage(page);
	// One zaal of this day, opened from its own tile: then the screen is that programma alone, as a list.
	// A zaal the day no longer has falls back to the whole day rather than to an empty screen.
	const stages = (programDay?.data.stages ?? []).map((stage) => stage.title);
	const stage = route?.params?.stage !== undefined && stages.includes(route.params.stage) ? route.params.stage : '';
	// Nothing hearted yet: then the placeholder gets the whole area to centre itself in, instead of sitting
	// against the nav bar.
	const timetableIsEmpty =
		isMyTimetable && !myTimeTableStore.myTimeTableData.some((i) => i.eventUuid === (programHolder?.event_uuid ?? ''));
	// The bar names what the screen shows: one zaal, or the day itself.
	const screenTitle = stage || title;
	// The programme decides how its days are shown: one choice for all of them, made where the tiles above
	// the programmes are put together. A day that carries its own older choice falls back on it, so an event
	// from before the setting moved keeps the view it had.
	const programView = programHolder?.data.program_view ?? programDay?.data.program_view ?? 'list';
	// Without a block schedule, or without a day that has times to place the blocks on, there is nothing to show
	// as blocks, whatever the holder asks for: program items without a time are normal, and they would render as
	// zero-width blocks nobody can tap.
	// Only a day with stages offers blocks: an existing day without them keeps looking exactly like the list it was.
	const hasStages = (programDay?.data.stages ?? []).some((stage) => Boolean(stage.title));
	const canShowBlocks = !isMyTimetable && !stage && hasStages && blocks.length > 0 && blocksEnd > blocksStart;
	// Under 'both' a day with stages opens on its blocks, so a converted blokkenschema looks the way it always did.
	// A programme without day tiles ('none') has no blocks view to open; reached by a deeplink such a day is a list.
	const showBlocks = canShowBlocks && (prefersBlocks ?? (programView === 'blocks' || programView === 'both'));
	const showViewSwitch = canShowBlocks && programView === 'both';

	// '' is a valid "no active event" sentinel, so truthiness (not nullish) is intended here.
	// From a URL the event is not active yet, so the page we resolved carries the uuid.
	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	const activeEventUuid = eventStore.activeEventUuid || page?.event_uuid || '';

	// Keyed on the uuid, so a cold load still runs this once the resolved page reveals which event
	// this program belongs to. In the app the uuid is known on mount, so it stays a single run.
	useEffect(() => {
		if (!activeEventUuid) {
			return;
		}

		if (isMyTimetable) {
			analyticsStore.timeTable(activeEventUuid);
		}

		// Server is leading for favorites: reconcile on every entry to the screen,
		// also on the common path where the program holder is already in memory.
		myTimeTableStore
			.syncFromServer(activeEventUuid)
			.catch(logError)
			.finally(() => {
				setFavoritesSyncedFor(activeEventUuid);
			});
		myBarTimeTableStore.syncFromServer(activeEventUuid, eventStore.pages ?? []).catch(logError);

		if (programLoaded) return;

		eventStore
			.fetchEvent(activeEventUuid)
			.catch(logError)
			.finally(() => {
				setHasFetched(true);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeEventUuid]);

	const handleRefresh = async () => {
		setRefreshing(true);
		await eventStore.fetchEvent(activeEventUuid, true);
		setRefreshing(false);
	};

	/** A heading in Mijn timetable. Its key is the day it came from, so two days named the same stay apart. */
	type SectionHeading = { key: string; label: string };
	type ProgramList = Array<ApiPageProgramItem | SectionHeading>;

	// An item without times has no overlap to report: falling back to 0 made it start in 1970, which is before
	// everything, so every untimed item was flagged red (#404). Parallel sessions in different stages are not an
	// overlap either: only neighbours in the same stage (or both without one) count.
	const detectOverlap = (item: ApiPageProgramItem, list: ProgramList, index: number) => {
		const start = item.data.start_time;
		const end = item.data.end_time;

		if (!start || !end) return false;

		// The nearest item in the same stage, skipping the other stages interleaved between them.
		const neighbour = (step: number) => {
			for (let i = index + step; i >= 0 && i < list.length; i += step) {
				const other = list[i];
				if (!other || !('data' in other)) return undefined;
				if (other.data.stage === item.data.stage) return other;
			}
			return undefined;
		};
		const prevEnd = neighbour(-1)?.data.end_time;
		const nextStart = neighbour(1)?.data.start_time;

		return (!!prevEnd && start < prevEnd) || (!!nextStart && end > nextStart);
	};

	// A block mirrors a program item of this very day, so it opens the same page the list does, whether or not the
	// beheerder filled it: the session screen carries the title, the time, the zaal and the heart on its own, and
	// says "geen inhoud" where the text would be. A block whose page is not in memory is opened from the block
	// itself, so the participant still lands on that screen instead of a bare one.
	const handleBlockPress = (item: TimeTablePageItem) => {
		const target =
			programDay?.children.find((child) => child.id === item.page_id) ??
			({
				id: item.page_id ?? 0,
				event_id: programDay?.event_id ?? 0,
				event_uuid: item.eventUuid,
				parent_id: programDay?.id ?? null,
				order: 0,
				id_path: item.id_path ?? '',
				type: 'ProgramItem',
				name: item.title,
				children: [],
				contentblocks: [],
				data: {
					start_time: item.startdate ?? undefined,
					end_time: item.enddate ?? undefined,
					stage: item.stageTitle,
				},
			} satisfies ApiPageProgramItem);

		navigationStore.setNotificationOrigin('');
		navigatePage(navigation, target.type, target);
	};

	const renderViewSwitch = () => (
		<View style={styles.viewSwitch}>
			{[
				{ label: Translations.buttonProgramViewList, blocks: false },
				{ label: Translations.buttonProgramViewBlocks, blocks: true },
			].map((option) => {
				const active = showBlocks === option.blocks;
				return (
					<TouchableOpacity
						accessibilityRole="button"
						accessibilityState={{ selected: active }}
						key={option.label}
						style={[styles.viewSwitchOption, active ? styles.viewSwitchOptionActive : null]}
						onPress={() => setPrefersBlocks(option.blocks)}
					>
						<Text style={[styles.viewSwitchLabel, active ? styles.viewSwitchLabelActive : null]}>{option.label}</Text>
					</TouchableOpacity>
				);
			})}
		</View>
	);

	const renderProgramList = (nav: NativeStackNavigationProp<RootStackParamList>) => {
		if (!programHolder) {
			return (
				<EmptyState
					icon="calendar-outline"
					message={Translations.textNoProgramMessage}
					title={Translations.textNoProgramTitle}
				/>
			);
		}
		const renderItems = (items: ApiPageProgramItem[], underStage = false) =>
			items.map((programItem, index) => {
				const flag = detectOverlap(programItem, items, index);
				return (
					<ProgramItem
						data={programItem}
						flag={flag}
						hideStage={underStage}
						key={programItem.id}
						noBorderBottom={items.length - 1 === index}
						onPress={(page, pageData) => navigatePage(nav, page, pageData)}
					/>
				);
			});
		if (programDay?.children && programDay.children.length > 0) {
			// Opened as one zaal: only its sessions, and no section header above them, because the screen is
			// that zaal.
			if (stage) {
				const own = programDay.children.filter((c) => c.data.stage === stage);

				return own.length > 0 ? (
					renderItems(own, true)
				) : (
					<EmptyState
						icon="calendar-outline"
						message={Translations.textNoProgramMessage}
						title={Translations.textNoProgramTitle}
					/>
				);
			}

			const children = programDay.children;
			// A day with stages is every programma at once, so its list groups per programma the way the
			// blocks do, instead of interleaving all sessions into one anonymous stream.
			// A zaal without a name is no zaal, and the same zaal entered twice is one row in the blocks, so
			// the list reads the stages the way the blocks do: named, and each one once.
			const stageTitles = [...new Set((programDay.data.stages ?? []).map((s) => s.title).filter(Boolean))];
			if (stageTitles.length > 0) {
				const unstaged = children.filter((c) => !stageTitles.includes(c.data.stage ?? ''));
				const sections = stageTitles
					.map((title) => ({ title, items: children.filter((c) => c.data.stage === title) }))
					.filter((section) => section.items.length > 0);
				return (
					<>
						{unstaged.length > 0 ? renderItems(unstaged) : null}
						{sections.map((section) => (
							<View key={section.title}>
								<Text style={styles.spacer}>{section.title}</Text>
								{/* The header above already names the zaal, so the rows leave it out. */}
								{renderItems(section.items, true)}
							</View>
						))}
					</>
				);
			}
			return renderItems(children);
		}
		return (
			<EmptyState
				icon="calendar-outline"
				message={Translations.textNoProgramMessage}
				title={Translations.textNoProgramTitle}
			/>
		);
	};

	const renderTimeTableList = () => {
		if (!programHolder) return null;
		const favoProgramItems = myTimeTableStore.myTimeTableData.filter((i) => i.eventUuid === programHolder.event_uuid);

		if (favoProgramItems.length === 0) {
			return (
				<EmptyState
					icon="heart-outline"
					iconColor={Colors.red}
					message={
						page?.type === 'Program' && page.data.description
							? page.data.description
							: Translations.textNoActivitiesInTimetable
					}
				/>
			);
		}

		// Over several days the same zaal comes back every day, so there the section says which day it is; on a
		// single-day event the day would only be noise. A day without a date says nothing about how many days
		// the programme runs, so it does not make one.
		const dates = new Set(
			programDays
				.filter((day) => !isFavoritesPage(day))
				.map((day) => day.data.page_date ?? '')
				.filter(Boolean),
		);
		const isMultiDay = dates.size > 1;

		// One section per programma, in the order the event lists them. A day with zalen holds every programma at
		// once, so there the zaal names the section; a day that is a programma itself names it. A programma you
		// liked nothing in is left out altogether.
		const sections: Array<{ key: string; label: string; date: string; items: ApiPageProgramItem[] }> = [];
		programDays.forEach((day) => {
			if (isFavoritesPage(day)) return;

			const date = day.data.page_date ?? '';
			const liked = day.children.filter((item) => favoProgramItems.some((i) => i.pageid === item.id));

			liked.forEach((item) => {
				const stage = item.data.stage;
				const programma = stage && stage.length > 0 ? stage : day.name;
				// The day the beheerder named ("Dag 1") tells the participant more than its date does; a day that
				// is the programma itself has nothing to add, so there the date separates the days.
				const dayLabel = programma === day.name ? formatPageDate(date) : day.name;
				const label = isMultiDay && dayLabel ? `${programma} | ${dayLabel}` : programma;
				// Keyed on the day as well, so two days named the same stay two sections.
				const key = `${day.id}-${programma}`;
				const section = sections.find((s) => s.key === key);

				section ? section.items.push(item) : sections.push({ key, label, date, items: [item] });
			});
		});

		// Two days a beheerder gave the same name read as one section; then the date tells them apart.
		const arraylist: ProgramList = [];
		sections.forEach((section) => {
			const ambiguous = sections.some((other) => other.key !== section.key && other.label === section.label);
			const label = ambiguous && section.date ? `${section.label} | ${formatPageDate(section.date)}` : section.label;

			arraylist.push({ key: section.key, label });
			arraylist.push(..._.sortBy(section.items, (o) => o.data.start_time));
		});

		const arrayLength = arraylist.length - 1;
		return arraylist.map((programItem, index) => {
			if (!('data' in programItem)) {
				return (
					<View key={programItem.key} style={styles.list}>
						<Text style={styles.spacer}>{programItem.label}</Text>
					</View>
				);
			}
			const flag = detectOverlap(programItem, arraylist, index);
			return (
				<ProgramItem
					data={programItem}
					flag={flag}
					// The section above names the zaal already.
					hideStage={true}
					key={programItem.id}
					noBorderBottom={arrayLength === index}
					onPress={(type, d) => navigatePage(navigation, type, d)}
				/>
			);
		});
	};

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={screenTitle}
			/>
			{isLoading || isPageLoading || (isMyTimetable && favoritesSyncedFor !== activeEventUuid) ? (
				<Loader />
			) : (
				<View style={styles.body}>
					{showViewSwitch ? renderViewSwitch() : null}
					{showBlocks ? (
						<View style={styles.blocks}>
							<TimetableBlocks
								dayTitle={title}
								endtime={blocksEnd}
								eventUuid={programDay?.event_uuid ?? ''}
								starttime={blocksStart}
								timetable={blocks}
								onItemPress={handleBlockPress}
							/>
						</View>
					) : (
						<ScrollView
							contentContainerStyle={[styles.contentContainer, { paddingBottom: footerHeight }]}
							refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
							style={{ flex: 1 }}
						>
							{!isMyTimetable ? (
								programDay?.children && programDay.children.length > 0 ? (
									// The nav bar already names the day, so the list starts without a heading of its own.
									<View renderToHardwareTextureAndroid={true} style={[styles.list, { borderBottomWidth: 0 }]}>
										{renderProgramList(navigation)}
									</View>
								) : (
									renderProgramList(navigation)
								)
							) : (
								// The nav bar already says Mijn timetable, and the sections below name themselves.
								<View
									renderToHardwareTextureAndroid={true}
									style={[styles.list, { borderBottomWidth: 0, borderTopWidth: 0 }, timetableIsEmpty ? styles.fill : null]}
									testID="my-timetable-body"
								>
									{renderTimeTableList()}
								</View>
							)}
						</ScrollView>
					)}
				</View>
			)}
			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default ProgramScreen;

const styles = StyleSheet.create({
	body: {
		flex: 1,
	},
	blocks: {
		flex: 0.94,
	},
	viewSwitch: {
		flexDirection: 'row',
		backgroundColor: Colors.white,
		paddingHorizontal: Dimens.padding20,
		paddingVertical: Dimens.padding8,
	},
	viewSwitchOption: {
		flex: 1,
		alignItems: 'center',
		paddingVertical: Dimens.padding8,
		borderWidth: 1,
		borderColor: Colors.greyBorder,
	},
	viewSwitchOptionActive: {
		backgroundColor: Colors.greyBgAlt,
	},
	viewSwitchLabel: {
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.montserratRegular,
		color: Colors.greyTextDark,
	},
	viewSwitchLabelActive: {
		fontFamily: Fonts.montserratBold,
	},
	contentContainer: {
		flexGrow: 1,
	},
	fill: {
		flex: 1,
	},
	spacer: {
		fontSize: Dimens.fontSize17,
		fontFamily: Fonts.montserratBold,
		color: Colors.greyTextDark,
		// Same air above and below as in Mijn timetable, so a day header does not stick to the row above it, and
		// its text lines up with those rows.
		paddingHorizontal: Dimens.padding20,
		paddingVertical: Dimens.padding16,
	},
	list: {
		backgroundColor: Colors.white,
		borderBottomWidth: Platform.OS === 'android' ? 0 : StyleSheet.hairlineWidth,
		borderTopWidth: Platform.OS === 'android' ? 0 : StyleSheet.hairlineWidth,
		borderColor: Colors.overlay15,
	},
});
